import { useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { MagnifyingGlass } from '@/components/icons'
import { Form, FormFieldset } from '@/components/ui/form'
import { IconButton } from '@/components/ui/icon-button'
import { Input } from '@/components/ui/input'
import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellText,
	CellTextDate,
	CellTextIdr,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'

import { useAuth } from '@/libs/hooks/use-auth'
import { toDateSimple } from '@/libs/utils/format'

import { salesTransactionList } from '@/features/sales/api'
import type { ITransaction } from '@/features/sales/type'

import { useFilters } from '@/hooks/use-filter'

const searchParams = z.object({
	date: z.tuple([z.string(), z.string()]).nullish(),
	rc: z.string().optional(),
})

export const Route = createFileRoute('/_app/sale/')({
	validateSearch: searchParams,
	component: Component,
})

export function Component() {
	return (
		<PageLayout.Root>
			<PageLayout.Header breadcrumbs={[['/sale', 'Sales']]} />
			<PageCard>
				<PageCard.Header title="Transaksi Penjualan" />
				<FilterContentSection />
			</PageCard>
			<TableSection />
		</PageLayout.Root>
	)
}

function TableSection() {
	const { outletId } = useAuth((s) => s.outlet)
	const { filters } = useFilters(Route.id)

	const [start, end] = useMemo(() => {
		const date = filters.date || new Date()
		return Array.isArray(date) ? date : [date, date]
	}, [filters.date])

	const { isLoading, data } = useQuery(
		salesTransactionList.qry({
			oId: outletId,
			date: [toDateSimple(start), toDateSimple(end)],
		})
	)

	const columnFilters = useMemo(() => {
		return filters.rc ? [{ id: 'rc', value: filters.rc }] : []
	}, [filters.rc])

	const columns = useMemo(getColumns, [])
	const { table } = useDataTable({
		// @ts-expect-error
		columns,
		data: data?.transactions || [],
		state: { columnFilters },
	})

	return (
		<PageCard.Root>
			<datalist id="rc-list">
				{data?.transactions.map((x) => <option value={x.rc} />)}
			</datalist>
			<PageCard.Content className="divide-none">
				{/* @ts-expect-error */}
				<DataTable table={table} columns={columns} isLoading={isLoading} />
			</PageCard.Content>
		</PageCard.Root>
	)
}

function FilterContentSection() {
	const { filters, setFilters } = useFilters(Route.id)
	const form = useForm({
		resolver: zodResolver(searchParams),
		defaultValues: filters,
		mode: 'all',
	})

	const { control } = form

	const onApply = form.handleSubmit((data) => setFilters(data))

	return (
		<Form {...form}>
			<PageCard.Content>
				<PageCard.ContentSection className="flex flex-wrap py-3">
					<Form.Field
						control={control}
						name="date.0"
						render={({ field }) => (
							<FormFieldset.One label="Mulai dari" className="min-w-36">
								<Input
									onChange={(e) => {
										const val = e.target.value
										field.onChange(val)
										if (val && val > form.getValues('date.1')) {
											form.setValue('date.1', val.toString())
										}
									}}
									value={field.value || ''}
									type="date"
									className="w-full"
								/>
							</FormFieldset.One>
						)}
					/>
					<Form.Field
						control={control}
						name="date.1"
						render={({ field }) => (
							<FormFieldset.One label="Sampai ke" className="min-w-36">
								<Input
									onChange={(e) => field.onChange(e.target.value)}
									value={field.value || ''}
									type="date"
									className="w-full"
								/>
							</FormFieldset.One>
						)}
					/>
					<Form.Field
						control={control}
						name="rc"
						render={({ field }) => (
							<FormFieldset.One label="Receipt Code">
								<Input
									onChange={(e) => field.onChange(e.target.value)}
									value={field.value || ''}
									className="w-full"
									placeholder="Cari receipt code"
									list="rc-list"
								/>
							</FormFieldset.One>
						)}
					/>
					<IconButton
						variant="primary"
						onClick={onApply}
						className="mt-auto"
						// disabled={!form.formState.isValid}
					>
						<MagnifyingGlass />
					</IconButton>
				</PageCard.ContentSection>
			</PageCard.Content>
		</Form>
	)
}

const ch = createColumnHelper<ITransaction>()
function getColumns() {
	return [
		ch.accessor('rc', {
			header: 'Receipt Code',
			cell: ({ row }) => <CellText text={row.original.rc} />,
		}),
		ch.accessor('ts', {
			header: 'Tanggal',
			cell: ({ row }) => <CellTextDate date={row.original.ts} />,
		}),
		ch.accessor('items.product.name', {
			header: 'Item',
			cell: ({ row }) => {
				return (
					<CellText
						text={row.original.items
							.map((item) => item.product.name)
							.join(', ')}
					/>
				)
			},
		}),
		ch.accessor('gross', {
			header: 'Gross',
			cell: ({ row }) => <CellTextIdr idr={row.original.gross} />,
		}),
		ch.accessor('cogs', {
			header: 'COGS',
			cell: ({ row }) => <CellTextIdr idr={row.original.cogs} />,
		}),
		ch.accessor('net', {
			header: 'Net',
			cell: ({ row }) => <CellTextIdr idr={row.original.net} />,
		}),
	]
}
